"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuLayout, NavigationMenuList } from "@/components/ui/navigation-menu";
const staticMenuItems = [{
  label: "Početna",
  href: "/HomePage"
}, {
  label: "Usluge",
  href: "/ServicesPage"
}, {
  label: "Servis baterija",
  href: "/BatteryServicePage"
}, {
  label: "Trgovina",
  href: "/ShopPage"
}, {
  label: "Cijene",
  href: "/PricesPage"
}, {
  label: "Kontakt",
  href: "/ContactPage"
}];
function DesktopMenuConsumer() {
  const {
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {staticMenuItems.map((item, i) => <NavigationMenuItem key={i} data-index={i}>
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-foreground hover:text-foreground focus:text-foreground">
              <Link to={item.href} className="group relative px-4 py-2 font-default text-sm font-medium text-foreground transition-colors">
                {item.label}
                <span className="absolute bottom-1 left-4 right-4 h-[2px] origin-left scale-x-0 bg-ring transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col py-4">
      {staticMenuItems.map((item, i) => <div key={i} data-index={i}>
          <Link to={item.href} className="block px-6 py-4 font-default text-lg font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
      <div className="px-6 py-4 mt-2 border-t border-border">
        <div className="flex flex-col gap-4">
          <Button asChild className="w-full bg-ring text-primary-foreground hover:bg-ring/90" size="lg">
            <Link to="#" onClick={onItemClick}>
              Rezerviraj popravak
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const script = document.createElement("script");
    script.innerHTML = `
      window.gtranslateSettings = {
        "default_language":"hr",
        "languages":["hr","en"],
        "wrapper_selector":".gtranslate_wrapper",
        "alt_flags":{"en":"usa"}
      };
    `;
    document.body.appendChild(script);
    const mainScript = document.createElement("script");
    mainScript.src = "https://cdn.gtranslate.net/widgets/latest/lc.js";
    mainScript.defer = true;
    document.body.appendChild(mainScript);
    return () => {
      document.body.removeChild(script);
      document.body.removeChild(mainScript);
    };
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === "/HomePage" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  return <section id="zaglavlje" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-border"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-20 items-center justify-between">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576390/img/perfect_hands_logo_header.png" alt="ChargeZone" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          <div className="hidden md:flex flex-1 justify-center items-center gap-4">
            <MenuProvider menu_id="61">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden md:flex flex-shrink-0 items-center gap-4">
            <CartIcon variant="ghost" size="icon" className="text-foreground" />
            <Button asChild variant="default" className="bg-ring text-primary-foreground hover:bg-ring/90 font-default font-medium tracking-wide rounded-sm shadow-sm transition-all duration-300">
              <Link to="#">Rezerviraj popravak</Link>
            </Button>
            <div className="gtranslate_wrapper flex items-center gap-2 font-default text-sm font-medium text-foreground/60">
            </div>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <CartIcon variant="ghost" size="icon" className="text-foreground" />
            <Button variant="ghost" size="icon" onClick={toggleMobileMenu} aria-label="Toggle navigation menu" className="text-foreground hover:bg-accent/50">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      <div className={`absolute left-0 top-full w-full origin-top bg-background border-b border-border shadow-lg transition-all duration-300 ease-in-out md:hidden ${isMobileMenuOpen ? "opacity-100 scale-y-100 visible" : "opacity-0 scale-y-0 invisible"}`}>
        <MenuProvider menu_id="61">
          <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}